/**
 * Fetches per-content lexicon overview docs (`GET /v1/:learnLang/content-stats`) for card stripes.
 * Call {@link ensureLexiconStatsHydrated} after {@link ensureContentApiHydrated} when both APIs are used.
 */

import { CONTENT_I18N_LANGS } from "./locale-registry.js";

/** Locales aligned with `content-api-runtime` hydration. */
const FETCH_LOCALES = [...CONTENT_I18N_LANGS];

/** @type {Map<string, Record<string, unknown>>} */
const overviewByLocale = new Map();
/** Locales whose last fetch failed and should be retried. */
const failedLocales = new Set();
let hydratePromise = null;

export function lexiconApiOrigin() {
  return String(process.env.LEXICON_API_URL ?? "http://localhost:8002").trim().replace(/\/$/, "");
}

export function isLexiconStatsApiEnabled() {
  return Boolean(lexiconApiOrigin());
}

function pendingLocales() {
  return FETCH_LOCALES.filter(
    (locale) => !overviewByLocale.has(locale) || failedLocales.has(locale),
  );
}

async function fetchOverviewForLocale(origin, locale) {
  const url = `${origin}/v1/${encodeURIComponent(locale)}/content-stats`;
  let res;
  try {
    res = await fetch(url);
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    console.warn(`[lexicon-stats] ${locale}: ${msg}`);
    overviewByLocale.set(locale, {});
    failedLocales.add(locale);
    return;
  }
  if (!res.ok) {
    console.warn(`[lexicon-stats] GET ${url} → ${res.status}`);
    overviewByLocale.set(locale, {});
    failedLocales.add(locale);
    return;
  }
  const body = /** @type {{ items?: Record<string, unknown> }} */ (await res.json());
  const items = body.items && typeof body.items === "object" ? body.items : {};
  overviewByLocale.set(locale, items);
  failedLocales.delete(locale);
}

async function runHydrate(locales) {
  const origin = lexiconApiOrigin();
  await Promise.all(locales.map((locale) => fetchOverviewForLocale(origin, locale)));
  try {
    const mod = await import("./lexicon-content-stats-map.js");
    if (typeof mod.resetLexiconStatsMapMemo === "function") mod.resetLexiconStatsMapMemo();
  } catch {
    /* ignore */
  }
}

/**
 * Loads lexicon content-stats payloads (`LEXICON_API_URL`, default `http://localhost:8002`).
 * Failed locales are retried on later calls so a lexicon outage at process start does not
 * leave word/level stripes empty for the rest of the server lifetime.
 * Set `LEXICON_API_URL=` empty to skip.
 */
export async function ensureLexiconStatsHydrated() {
  if (!isLexiconStatsApiEnabled()) return;
  if (hydratePromise) await hydratePromise;
  const locales = pendingLocales();
  if (!locales.length) return;
  hydratePromise = runHydrate(locales);
  try {
    await hydratePromise;
  } finally {
    hydratePromise = null;
  }
}

export function getLexiconOverviewItems(locale) {
  if (!isLexiconStatsApiEnabled()) return null;
  const k = String(locale || "de").toLowerCase();
  if (overviewByLocale.has(k)) return overviewByLocale.get(k) || {};
  return null;
}

/** Test helper: clear in-memory overview cache and failure flags. */
export function resetLexiconStatsHydrationForTests() {
  overviewByLocale.clear();
  failedLocales.clear();
  hydratePromise = null;
}
